import { useState, useCallback, useMemo } from 'react';
import { useAPI } from './useAPIs';
import { useFileApi } from './useFileApi';
import { Snackbar, Alert } from '@mui/material';
import * as XLSX from 'xlsx';

export const useFileCompare = (data) => {
  const [condition, setCondition] = useState(false);
  const [comparing, setComparing] = useState(false);
  const { getBlobFromUrl } = useAPI();
  const { getMultipleDownloadUrls } = useFileApi();
  const [errorMessage, setErrorMessage] = useState('');

  const rowIdToFilename = useMemo(() => {
    const mapping = {};
    data.forEach(row => {
      const rowId = row.id ?? `${row.vin}-${row.datetime}`;
      mapping[rowId] = row.file_location;
    });
    return mapping;
  }, [data]);
  
  const getSelectedFilenames = useCallback((rowSelection) => {
    return Object.keys(rowSelection).map(rowId => rowIdToFilename[rowId]).filter(Boolean);
  }, [rowIdToFilename]);

  const compareMultipleFiles = useCallback(async (filenames) => {
    const fileUrls = await getMultipleDownloadUrls(filenames);
    const urlMap = new Map(fileUrls.map(f => [f.path, f.url]));

    const comparePromises = filenames.map(async (file) => {
      try {
        const downloadUrl = urlMap.get(file);
        if (!downloadUrl) {
          throw new Error(`No download URL found for ${file}`);
        }
        const blob = await getBlobFromUrl(downloadUrl);
        return blob;
      } catch (error) {
        console.error("Error fetching file:", file, error);
        throw error;
      }
    });

    return await Promise.all(comparePromises);
  }, [getBlobFromUrl, getMultipleDownloadUrls]);

  const readXlsxAsText = async (blob) => {
    try {
      const arrayBuffer = await blob.arrayBuffer();
      const workbook = XLSX.read(arrayBuffer, { type: 'array' });
      
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      
      const csvText = XLSX.utils.sheet_to_csv(worksheet);
      return csvText;
    } catch (error) {
      console.error('Error reading XLSX file:', error);
      throw error;
    }
  };

  const handleCompare = useCallback(async (rowSelection) => {
    const selectedFilenames = getSelectedFilenames(rowSelection);

    if (selectedFilenames.length !== 2) {
      setErrorMessage(
        selectedFilenames.length < 2 
          ? `Please select 2 files. Currently selected: ${selectedFilenames.length}` 
          : `Too many files selected: ${selectedFilenames.length}. Please select only 2 files.`
      );
      setCondition(true);
      return;
    }
    

    const compressedExts = ['.gz', '.zip', '.rar', '.7z', '.tar', '.bz2'];
    const foundCompressed = selectedFilenames.some(filename => {
      const parts = filename.toLowerCase().split('.');
      const fileExtension = parts.length > 1 ? `.${parts.pop()}` : '';
      return compressedExts.includes(fileExtension);
    });

    if (foundCompressed) {
      setErrorMessage(
        `Compressed file types (.gz, .zip, .rar, .7z, .tar, .bz2) cannot be previewed. Select unzipped files only.`
      );
      setCondition(true);
      return;
    }
  
    try {
      setComparing(true);
      const files = await compareMultipleFiles(selectedFilenames);
      
      if (!files || files.length !== 2) {
        throw new Error("Failed to retrieve both files");
      }
      
      const readBlobAsText = (blob, filename) => {
        return new Promise((resolve, reject) => {
          if (!blob || !(blob instanceof Blob)) {
            reject(new Error("Invalid blob provided"));
            return;
          }
          
          if (!filename || typeof filename !== 'string') {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.onerror = () => reject(reader.error);
            reader.readAsText(blob, 'UTF-8');
            return;
          }
          
          const parts = filename.toLowerCase().split('.');
          const fileExtension = parts.length > 1 ? parts.pop() : '';
          
          if (fileExtension === 'xlsx' || fileExtension === 'xls') {
            readXlsxAsText(blob).then(resolve).catch(reject);
          } else {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.onerror = () => reject(reader.error);
            reader.readAsText(blob, 'UTF-8');
          }
        });
      };

      const [content1, content2] = await Promise.all([
        readBlobAsText(files[0], selectedFilenames[0]),
        readBlobAsText(files[1], selectedFilenames[1])
      ]);
      

      
      sessionStorage.setItem('compareData', JSON.stringify({
        content1,
        content2,
        filename1: selectedFilenames[0],
        filename2: selectedFilenames[1]
      }));

      window.open('/file-compare', '_blank');
        
    } catch (error) {
      setCondition(true);
      setErrorMessage(error.message || 'Error comparing files');
      console.error('Error comparing files:', error);
    } finally {
      setComparing(false);
    }
  }, [getSelectedFilenames, compareMultipleFiles]);

  const CustomSnackbar = () => (
    <Snackbar
      open={condition}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      autoHideDuration={2000}
      onClose={() => setCondition(false)}
      sx={{ 
        zIndex: 10000,
        top: '80px !important',
        '& .MuiPaper-root': {
          zIndex: 10000
        }
      }}
    >
      <Alert 
        severity="warning"
        variant="filled"
        sx={{ 
          width: '100%',
          minWidth: '400px',
          fontSize: '14px',
          fontWeight: 500,
          boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.15)',
          '& .MuiAlert-icon': {
            fontSize: '20px'
          }
        }}
      >
        {errorMessage || 'Please select exactly 2 files to compare'}
      </Alert>
    </Snackbar>
  );

  return {
    comparing,
    handleCompare,
    getSelectedFilenames,
    CustomSnackbar 
  };
};
